import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import Log from '../utils/Log';
import { toApiError } from '../utils/apiError';

// Async thunks
export const fetchAllDeals = createAsyncThunk(
  'deals/fetchAll',
  async ({ page = 1, limit = 50, category, sort = 'relevance', tags = [] } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
      if (category && category !== 'all') {
        params.append('category', category);
      }
      if (sort && sort !== 'relevance') {
        params.append('sort', sort);
      }
      if (tags && tags.length > 0) {
        params.append('tags', tags.join(','));
      }
      // Use /best endpoint for curated homepage deals with variety and quality scoring
      const response = await axios.get(`/api/deals/best?${params.toString()}`);
      Log.trace(`Fetched ${response.data.deals?.length || 0} best deals on page ${page}${category ? ` for category: ${category}` : ''}`);
      return response.data;
    } catch (error) {
      Log.error('Failed while fetching deals');
      return rejectWithValue(toApiError(error));
    }
  },
);

export const searchDeals = createAsyncThunk(
  'deals/search',
  async ({ query: searchQuery, page = 1, limit = 50, category, sort = 'relevance', tags = [] }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/deals/search', {
        query: searchQuery,
        page,
        limit,
        category,
        sort,
        tags: tags && tags.length > 0 ? tags : undefined,
      });
      Log.trace(`Found ${response.data.deals?.length || 0} deals for search: "${searchQuery}" on page ${page}${category ? ` in category: ${category}` : ''}`);
      return response.data;
    } catch (error) {
      Log.error('Failed while searching deals');
      return rejectWithValue(toApiError(error));
    }
  },
);

export const loadMoreDeals = createAsyncThunk(
  'deals/loadMore',
  async ({ page, limit = 50, searchQuery, category, sort = 'relevance', tags = [] }, { rejectWithValue }) => {
    try {
      let response;
      if (searchQuery && searchQuery.trim() !== '') {
        // Use search endpoint for search queries
        response = await axios.post('/api/deals/search', {
          query: searchQuery,
          page,
          limit,
          category,
          sort,
          tags: tags && tags.length > 0 ? tags : undefined,
        });
      } else {
        // Use /best endpoint for curated homepage deals
        const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
        if (category && category !== 'all') {
          params.append('category', category);
        }
        if (sort && sort !== 'relevance') {
          params.append('sort', sort);
        }
        if (tags && tags.length > 0) {
          params.append('tags', tags.join(','));
        }
        response = await axios.get(`/api/deals/best?${params.toString()}`);
      }
      Log.trace(`Loaded ${response.data.deals?.length || 0} more deals on page ${page}${category ? ` for category: ${category}` : ''}`);
      return response.data;
    } catch (error) {
      Log.error('Failed while loading more deals');
      return rejectWithValue(toApiError(error));
    }
  },
);

export const generatePost = createAsyncThunk(
  'deals/generatePost',
  async ({ data, groups }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/deals/generate', { data, groups });
      Log.trace(`Posted in ${groups.length} groups`);
      return { resData: response.data, isRes: true };
    } catch (error) {
      Log.error('Error while posting data');
      return rejectWithValue(toApiError(error));
    }
  },
);

const dealSlice = createSlice({
  name: 'deals',
  initialState: {
    list: [],
    loading: false,
    loadingMore: false,
    error: null,
    modal: {},
    postData: null,
    isRes: false,
    currentSearchTerm: '',
    currentCategory: 'all',
    currentSort: 'relevance',
    activeFilters: [], // tag pills ('lowest_price', 'best_seller'); lives here so pills and list never disagree
    loadMoreError: null, // kept separate so a failed Load More doesn't hide the deals already shown
    listRequestId: null, // requestId of the fetch/search that owns state.list (stale responses are ignored)
    loadMoreRequestId: null,
    pagination: {
      currentPage: 1,
      totalPages: 0,
      totalDeals: 0,
      limit: 50,
      hasNextPage: false,
      hasPrevPage: false,
      nextPage: null,
      prevPage: null,
    },
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    clearPostData: (state) => {
      state.postData = null;
      state.isRes = false;
    },
    setModal: (state, action) => {
      state.modal = action.payload;
    },
    clearModal: (state) => {
      state.modal = {};
    },
    setSearchTerm: (state, action) => {
      state.currentSearchTerm = action.payload;
    },
    clearSearchTerm: (state) => {
      state.currentSearchTerm = '';
    },
    setCategory: (state, action) => {
      state.currentCategory = action.payload;
    },
    clearCategory: (state) => {
      state.currentCategory = 'all';
    },
    setSort: (state, action) => {
      state.currentSort = action.payload;
    },
    setActiveFilters: (state, action) => {
      state.activeFilters = Array.isArray(action.payload) ? action.payload : [];
    },
    clearLoadMoreError: (state) => {
      state.loadMoreError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all deals
      .addCase(fetchAllDeals.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.loadMoreError = null;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(fetchAllDeals.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return; // a newer fetch/search superseded this one
        state.loading = false;
        state.list = action.payload.deals || [];
        state.pagination = action.payload.pagination || state.pagination;
        state.currentSearchTerm = ''; // Clear search term when showing all deals
      })
      .addCase(fetchAllDeals.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.error = action.payload;
      })
      // Search deals
      .addCase(searchDeals.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.loadMoreError = null;
        state.listRequestId = action.meta.requestId;
        state.currentSearchTerm = action.meta.arg.query || ''; // Store the search term
      })
      .addCase(searchDeals.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.list = action.payload.deals || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(searchDeals.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.error = action.payload;
      })
      // Load more deals
      .addCase(loadMoreDeals.pending, (state, action) => {
        state.loadingMore = true;
        state.loadMoreError = null;
        state.loadMoreRequestId = action.meta.requestId;
      })
      .addCase(loadMoreDeals.fulfilled, (state, action) => {
        // Ignore a page that belongs to a list the user has since replaced (new category/sort/search)
        if (action.meta.requestId !== state.loadMoreRequestId || action.meta.arg.listRequestId !== state.listRequestId) return;
        state.loadingMore = false;
        const seen = new Set(state.list.map((d) => d._id));
        const newDeals = (action.payload.deals || []).filter((d) => !seen.has(d._id));
        state.list = [...state.list, ...newDeals];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(loadMoreDeals.rejected, (state, action) => {
        if (action.meta.requestId !== state.loadMoreRequestId) return;
        state.loadingMore = false;
        state.loadMoreError = action.payload;
      })
      // Generate post
      .addCase(generatePost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generatePost.fulfilled, (state, action) => {
        state.loading = false;
        state.postData = action.payload.resData;
        state.isRes = action.payload.isRes;
      })
      .addCase(generatePost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearErrors, clearPostData, setModal, clearModal, setSearchTerm, clearSearchTerm,
  setCategory, clearCategory, setSort, setActiveFilters, clearLoadMoreError,
} = dealSlice.actions;
export default dealSlice.reducer;
